import type { Hero } from '../types';
import { calculateWinRate } from './calculateWinRate';
import type { DraftSlot, PickEvent } from './draftTypes';

const SLOT_COUNT = 5;

function emptyRow(): DraftSlot[] {
  return Array.from({ length: SLOT_COUNT }, () => ({}));
}

function heroesFromSlots(slots: DraftSlot[], byId: Map<number, Hero>): Hero[] {
  const out: Hero[] = [];
  for (const s of slots) {
    if (s.heroId === undefined) continue;
    const h = byId.get(s.heroId);
    if (h) {
      out.push(h);
    }
  }
  return out;
}

/**
 * ピック履歴からスロットとピック時点の貢献度を再計算する（WIN_RATE_UPDATE §4）
 */
export function replaySlotsFromEvents(
  events: PickEvent[],
  heroesById: Map<number, Hero>
): { allySlots: DraftSlot[]; enemySlots: DraftSlot[] } {
  const allySlots = emptyRow();
  const enemySlots = emptyRow();
  const sorted = [...events].sort((a, b) => a.seq - b.seq);

  for (const ev of sorted) {
    const hero = heroesById.get(ev.heroId);
    if (!hero) {
      continue;
    }

    const beforeAlly = heroesFromSlots(allySlots, heroesById);
    const beforeEnemy = heroesFromSlots(enemySlots, heroesById);

    if (ev.team === 'ally') {
      const beforePa = calculateWinRate(beforeAlly, beforeEnemy);
      const afterPa = calculateWinRate([...beforeAlly, hero], beforeEnemy);
      const contribution = afterPa - beforePa;
      allySlots[ev.slotIndex] = {
        heroId: hero.id,
        heroName: hero.name,
        headUrl: hero.head_url || '',
        pickSeq: ev.seq,
        contribution,
      };
    } else {
      const beforePa = calculateWinRate(beforeAlly, beforeEnemy);
      const afterPa = calculateWinRate(beforeAlly, [...beforeEnemy, hero]);
      const beforePe = 1 - beforePa;
      const afterPe = 1 - afterPa;
      const contribution = afterPe - beforePe;
      enemySlots[ev.slotIndex] = {
        heroId: hero.id,
        heroName: hero.name,
        headUrl: hero.head_url || '',
        pickSeq: ev.seq,
        contribution,
      };
    }
  }

  return { allySlots, enemySlots };
}

export function nextPickSeq(events: PickEvent[]): number {
  if (events.length === 0) {
    return 1;
  }
  return Math.max(...events.map(e => e.seq)) + 1;
}

export function pickedHeroIdSet(events: PickEvent[]): Set<number> {
  return new Set(events.map(e => e.heroId));
}
